
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { LogOut, BookOpen, FileText, Users } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import OperatorTasks from '../components/operator/OperatorTasks'
import OperatorResources from '../components/operator/OperatorResources'
import OperatorStudents from '../components/operator/OperatorStudents'

const OperatorDashboard = () => {
  const [activeTab, setActiveTab] = useState('tasks')
  const [operatorInfo, setOperatorInfo] = useState<any>(null)
  const navigate = useNavigate()
  const { toast } = useToast()

  useEffect(() => {
    const fetchOperatorInfo = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        const { data: operator } = await supabase
          .from('operators')
          .select(`
            *,
            schools (
              id,
              name
            )
          `)
          .eq('user_id', session.user.id)
          .single()
        
        setOperatorInfo(operator)
      }
    }

    fetchOperatorInfo()
  }, [])

  const handleSignOut = async () => {
    console.log('Attempting to sign out...')
    
    try {
      // Clear local state immediately
      setOperatorInfo(null)
      
      // Check if there's a session first
      const { data: { session } } = await supabase.auth.getSession()
      
      if (session) {
        // There's a valid session, try to sign out normally
        const { error } = await supabase.auth.signOut()
        
        if (error && error.message !== 'Auth session missing!') {
          console.error('Sign out error:', error)
          toast({
            title: "Sign out error",
            description: error.message,
            variant: "destructive",
          })
        } else {
          console.log('Sign out successful or session already cleared')
        }
      } else {
        console.log('No session found, proceeding with cleanup')
      }
      
      // Always show success message and navigate
      toast({
        title: "Signed out",
        description: "You have been successfully signed out.",
      })
      
      // Force navigation to login page
      navigate('/operator/login', { replace: true })
      
    } catch (error: any) {
      console.error('Unexpected sign out error:', error)
      
      // Even on error, clear local state and navigate
      setOperatorInfo(null)
      
      // Only show error if it's not a session missing error
      if (!error.message?.includes('Auth session missing')) {
        toast({
          title: "Sign out completed",
          description: "You have been signed out.",
        })
      }
      
      // Force navigation regardless of error
      navigate('/operator/login', { replace: true })
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">Operator Dashboard</h1>
              {operatorInfo && (
                <span className="ml-4 text-sm text-gray-500">
                  {operatorInfo.schools?.name}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Welcome, {operatorInfo?.first_name} {operatorInfo?.last_name}
              </span>
              <Button
                onClick={handleSignOut}
                variant="outline"
                className="flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="tasks" className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Tasks
            </TabsTrigger>
            <TabsTrigger value="resources" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Resources
            </TabsTrigger>
            <TabsTrigger value="students" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Students
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tasks" className="space-y-4">
            <OperatorTasks />
          </TabsContent>

          <TabsContent value="resources" className="space-y-4">
            <OperatorResources />
          </TabsContent>

          <TabsContent value="students" className="space-y-4">
            <OperatorStudents />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

export default OperatorDashboard
